import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  computed,
  effect,
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  REQUEST,
  signal,
  TransferState,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { Request } from 'express';
import QRCode from 'qrcode';
import { firstValueFrom, from, Observable } from 'rxjs';
import { StorageService } from '../../core/services/storage/storage.service';
import { InstallationDetailsRoot } from '../../interfaces/installation';
import { THEME_SIGNAL } from '../../interfaces/theme';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { SubscriberService } from '../../ssr-services/subscriber/subscriber.service';
import { INSTALLATION_CONTENT_TOKEN, InstallationContent } from '../../tokens/server-tokens';

const INSTALLATION_TRANSFER_KEY = makeStateKey<InstallationDetailsRoot>(
  'INSTALLATION_TRANSFER_KEY'
);

const EMPTY_INSTALLATION_DATA: InstallationDetailsRoot = {
  installation_heading: {
    installation_heading_web: '',
  },
  installation_sub_heading: {
    installation_sub_details_web_one: '',
    installation_sub_details_web_two: '',
  },
  device_list: {
    device_list_title: '',
    list: {
      ios: '',
      samsung: '',
      google_pixel: '',
      other_devices: '',
    },
  },
  other_devices_list: {
    device_list_title: '',
    list: {
      oppo: '',
      motorola: '',
      gemini: '',
      huawei: '',
      oneplus: '',
      sony: '',
      xiaomi: '',
      rakuten: '',
      sharp: '',
      honor: '',
      nokia: '',
    },
  },
  devices: [],
  thank_you_messages: [],
};

@Injectable({
  providedIn: 'root',
})
export class InstallationService {
  public route = inject(ActivatedRoute);

  private readonly theme = inject(THEME_SIGNAL);

  public installationContent = signal<InstallationDetailsRoot>(EMPTY_INSTALLATION_DATA);

  public installationHeading = computed<InstallationDetailsRoot['installation_heading']>(() => {
    return this.installationContent().installation_heading || { installation_heading_web: '' };
  });

  public installationSubHeading = computed<InstallationDetailsRoot['installation_sub_heading']>(
    () => {
      return (
        this.installationContent().installation_sub_heading || {
          installation_sub_details_web_one: '',
          installation_sub_details_web_two: '',
        }
      );
    }
  );

  protected readonly installationContentManager = inject<InstallationContent | null>(
    INSTALLATION_CONTENT_TOKEN,
    {
      optional: true,
    }
  );

  protected cacheService = inject(CacheService);

  protected transferState = inject(TransferState);

  protected platform = inject(PLATFORM_ID);

  protected apiConfig = inject(ApiConfigService);

  protected http = inject(HttpClient);

  protected subscriberService = inject(SubscriberService);

  protected request: Request | null = inject<Request | null>(REQUEST);

  public isLoading = signal<boolean>(false);

  protected $document = inject(DOCUMENT);

  public storageService = inject(StorageService);

  public qrCodeDataUrl = signal('');

  public s3BucketAssets = computed(
    () => this.cacheService.cachedContent()?.cache?.config?.webAppConfig?.s3BucketAssets
  );

  public readonly faviconUrl = computed(() => {
    const s3BucketAssets = this.s3BucketAssets();
    if (!s3BucketAssets) {
      return '';
    }
    return (
      s3BucketAssets +
      `/${this.cacheService.cachedContent()?.cache?.customerId}/common/images/logo-square.png`
    );
  });

  public activationCodeForQr = signal<string | null>(null);

  public readonly smdpAddress = computed(() => {
    return this.activationCodeForQr()?.split('$')[1];
  });

  public readonly manualActivationCode = computed(() => {
    return this.activationCodeForQr()?.split('$')[2];
  });

  public readonly iosActivationLink = computed(() => {
    return this.apiConfig.getUniversalLink(this.activationCodeForQr() ?? '');
  });

  public readonly oneClickInstallAndroidEnabled = this.cacheService.oneClickInstallAndroidEnabled();

  constructor() {
    if (isPlatformBrowser(this.platform)) {
      this.installationContent.set(
        this.transferState.get(INSTALLATION_TRANSFER_KEY, EMPTY_INSTALLATION_DATA)
      );
    }

    effect(() => {
      this.subscriberService.subscriberLanguage();

      if (isPlatformServer(this.platform)) {
        return;
      }

      this.fetchFromApi().subscribe({
        next: (value) => {
          this.installationContent.set(value);
        },
        error: (err) => {
          console.error(err);
        },
      });
    });

    // Subscribe to route query params changes
    this.route.queryParams.subscribe((params) => {
      const activationCode = params['activationCode'] || null;
      this.activationCodeForQr.set(activationCode);
    });

    effect(() => {
      const activationCode = this.activationCodeForQr();
      if (activationCode && isPlatformBrowser(this.platform)) {
        this.generateQRCode(activationCode);
      }
    });
  }

  public fetchFromApi(): Observable<InstallationDetailsRoot> {
    const host = this.apiConfig.getHost();
    const cachedContent = this.cacheService.cachedContent();

    return this.http.get<InstallationDetailsRoot>(
      this.apiConfig.hostApiUrl('installation-translations'),
      {
        params: {
          host,
          s3Bucket: cachedContent?.cache.config.webAppConfig.s3BucketAssets || '',
          customerId: cachedContent?.cache.customerId || '',
          language: this.subscriberService.subscriberLanguage()?.code || '',
        },
      }
    );
  }

  public directFetchInServer(): Observable<InstallationDetailsRoot> {
    if (!this.installationContentManager) {
      throw new Error('Installation Content Manager is not initialized');
    }

    const cachedContent = this.cacheService.cachedContent();

    if (!cachedContent) {
      throw new Error('Cached Content is not initialized');
    }

    return from(
      this.installationContentManager.fetchInstallationContent(
        cachedContent.cache.config.webAppConfig.s3BucketAssets,
        cachedContent.cache.customerId,
        this.cacheService.getHost(),
        this.subscriberService.subscriberLanguage()?.code,
        this.theme()
      )
    );
  }

  /**
   * Initializes installation data for server
   * @returns Promise<InstallationDetailsRoot>
   */
  public async initInstallationInServer(): Promise<InstallationDetailsRoot> {
    const installation = await firstValueFrom(this.directFetchInServer());

    this.installationContent.set(installation);
    this.transferState.set(INSTALLATION_TRANSFER_KEY, installation);

    return installation;
  }

  /**
   * Get current installation data
   */
  public get installationData() {
    return this.installationContent;
  }

  /**
   * Force refresh installation data
   */
  public refreshInstallationData(): void {
    this.fetchFromApi().subscribe({
      next: (data) => {
        this.installationContent.set(data);
      },
      error: (err) => {
        console.error('Failed to refresh installation data:', err);
      },
    });
  }

  public generateQRCode(activationCode: string) {
    if (!isPlatformBrowser(this.platform)) {
      return;
    }

    if (!activationCode) {
      console.error('No session URL available for QR code generation');
      this.qrCodeDataUrl.set('');
      return;
    }

    const options = {
      width: 400,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    };

    QRCode.toDataURL(activationCode, options, (err, qrDataUrl) => {
      if (err) {
        console.error('QR Code generation error:', err);
        this.qrCodeDataUrl.set('');
        return;
      }

      // Try to add logo, fallback to plain QR code if it fails
      const logoUrl = this.faviconUrl();
      if (logoUrl && logoUrl.trim() !== '') {
        this.addLogoToQRCode(qrDataUrl, logoUrl);
      } else {
        console.warn('No favicon URL available, using plain QR code');
        this.qrCodeDataUrl.set(qrDataUrl);
      }
    });
  }

  private addLogoToQRCode(qrDataUrl: string, logoUrl: string) {
    const canvas = this.$document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Canvas not supported, using plain QR code');
      this.qrCodeDataUrl.set(qrDataUrl);
      return;
    }

    const qrImage = new Image();
    const logoImage = new Image();

    qrImage.crossOrigin = 'anonymous';
    logoImage.crossOrigin = 'anonymous';

    qrImage.onload = () => {
      // Set canvas size to match QR image
      canvas.width = qrImage.width;
      canvas.height = qrImage.height;

      // Draw QR code on canvas
      ctx.drawImage(qrImage, 0, 0);

      logoImage.onload = () => {
        // Calculate logo size (20% of QR code width)
        const logoSize = qrImage.width * 0.2;
        const x = (qrImage.width - logoSize) / 2;
        const y = (qrImage.height - logoSize) / 2;

        // Optional white background behind logo
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(x - 5, y - 5, logoSize + 10, logoSize + 10);

        // Draw logo
        ctx.drawImage(logoImage, x, y, logoSize, logoSize);

        // Convert to Data URL
        const finalDataUrl = canvas.toDataURL('image/png');
        this.qrCodeDataUrl.set(finalDataUrl);
      };

      logoImage.onerror = () => {
        console.warn('Logo failed to load, using plain QR');
        this.qrCodeDataUrl.set(qrDataUrl);
      };

      logoImage.src = logoUrl;
    };

    qrImage.onerror = () => {
      console.error('QR image failed to load');
      this.qrCodeDataUrl.set(qrDataUrl);
    };

    qrImage.src = qrDataUrl;
  }

  public downloadQrCode(qrCodeUrl: string) {
    const link = this.$document.createElement('a');
    link.target = '_blank';
    link.href = qrCodeUrl;
    link.download = `esim-qr-code-${Date.now()}.png`;

    this.$document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
